import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import BetaCodeRequired from './BetaCodeRequired';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const [loading, setLoading] = useState(true);
  const [hasBetaCode, setHasBetaCode] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkBetaAccess = async () => {
      try {
        // Get the current session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) throw sessionError;

        // If no user is logged in, allow access
        if (!session?.user) {
          setHasBetaCode(true);
          return;
        }

        // Check if user has a beta code
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('beta_code')
          .eq('id', session.user.id)
          .single();

        if (profileError) {
          console.error('Error fetching profile:', profileError);
          throw profileError;
        }

        // Only require beta code for Google-authenticated users without a beta code
        const isGoogleAuth = session.user.app_metadata.provider === 'google';
        setHasBetaCode(!isGoogleAuth || !!profile?.beta_code);
      } catch (error) {
        console.error('Error checking beta access:', error);
        setHasBetaCode(false);
      } finally {
        setLoading(false);
      }
    };

    checkBetaAccess();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Checking access...</span>
        </div>
      </div>
    );
  }

  if (!hasBetaCode) {
    return <BetaCodeRequired />;
  }

  return <>{children}</>;
} 